import { useIonAlert } from '@ionic/react';
import { useMemo } from 'react';
import { useScratchpadServiceContext } from '../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import i18n from '../../../../i18n';
import { getLocalizedText } from '../../../../i18n/i18n.utils';
import { localCache } from '../../../../util/cache';
import { FlowItem } from '../model/flow.types';
import { useFlowServices } from './flow-service.hooks';

export interface FlowUnsavedItemHelpers {
  setItems(items: FlowItem[]): Promise<void>;
  getItems(): Promise<FlowItem[] | null>;
  clear(): Promise<void>;
  present(cb: (items?: FlowItem[]) => void): Promise<void>;
}

const useFlowUnsavedItemHelpers = (
  cachePrefix: string
): FlowUnsavedItemHelpers => {
  const { scratchpad } = useScratchpadServiceContext();
  const { flowAnalyticService } = useFlowServices();

  const [presentAlert] = useIonAlert();

  const helpers = useMemo((): FlowUnsavedItemHelpers => {
    const key = `${cachePrefix}.${scratchpad.id}`;

    const clear = async () => {
      await localCache.removeItem(key);
    };

    const getItems = async () => {
      return localCache.getItem<FlowItem[]>(key);
    };

    const setItems = async (items: FlowItem[]) => {
      await localCache.setItem(key, items);
    };

    return {
      clear,
      getItems,
      setItems,
      present: async (cb: (items?: FlowItem[]) => void): Promise<void> => {
        const unsavedItems = await getItems();

        if (unsavedItems && unsavedItems.length > 0) {
          const message = getLocalizedText('common:unsaved_items', {
            numOfItems: unsavedItems.length,
          });

          presentAlert(message ?? '', [
            {
              text: i18n.t('common:discard_changes'),
              role: 'destructive',
              handler: async () => {
                await clear();
                flowAnalyticService.trackPresentedUnsavedItems(true);
                cb();
              },
            },
            {
              text: getLocalizedText('common:apply_changes') ?? '',
              handler: async () => {
                flowAnalyticService.trackPresentedUnsavedItems();
                cb(unsavedItems);
              },
            },
          ]);
        } else {
          cb();
        }
      },
    };
  }, [scratchpad, presentAlert, flowAnalyticService, cachePrefix]);

  return helpers;
};

export { useFlowUnsavedItemHelpers };
